/**
 * Remote session v1 — MQTT topic layout + wire JSON envelope.
 * @see docs/REMOTE-SESSION-ARCHITECTURE-V1.md
 */

export const REMOTE_V1_NAMESPACE = 'lectura/v1' as const;
export const REMOTE_V1_ENVELOPE_VERSION = 1 as const;

/** Retained session `state` uses this `mode` when the presenter has ended the remote session. */
export const REMOTE_V1_SESSION_MODE_ENDED = 'ended' as const;

export type RemoteV1WireKind =
  | 'state'
  | 'presence'
  | 'command'
  | 'emote'
  | 'participant_status';

export interface RemoteV1WireActor {
  participant_id: string;
  role: string;
}

/** MQTT JSON envelope (snake_case on the wire). */
export interface RemoteV1WireEnvelope {
  v: typeof REMOTE_V1_ENVELOPE_VERSION;
  t: RemoteV1WireKind;
  session_id: string;
  presentation_id: string;
  ts: number;
  actor?: RemoteV1WireActor;
  payload: unknown;
  /** Dev / pre-broker-ACL: HMAC or random token checked by presenter. */
  command_token?: string;
}

export interface RemoteV1Slide {
  h: number;
  v: number;
  f: number;
}

export interface RemoteV1StateMeta {
  title?: string;
  notes_plain?: string;
  slides_version?: string;
  total_slides?: number;
  highlight_color_rgb?: string;
  tts_lang?: string;
  notes_source_lang?: string;
}

/** Payload for `t: "state"` messages. */
export interface RemoteV1StatePayload {
  slide: RemoteV1Slide;
  mode: string;
  meta?: RemoteV1StateMeta;
  version: number;
  updated_at: string;
}

export type RemoteV1CommandType =
  | 'NAVIGATE_NEXT'
  | 'NAVIGATE_PREV'
  | 'NAVIGATE_UP'
  | 'NAVIGATE_DOWN'
  | 'SET_SLIDE'
  | 'TTS_RUN'
  | 'TTS_SETTINGS'
  | 'SET_MODE';

export interface RemoteV1CommandPayload {
  type: RemoteV1CommandType;
  slide?: RemoteV1Slide;
  codeword?: string;
  lang?: string;
  mode?: string;
}

export interface RemoteV1EmotePayload {
  emoji: string;
  label?: string;
  ttl_ms?: number;
  /** Visual intensity on presenter (1 = normal tap, >1 = long-press “boost”). Clamped 1–2.25 on parse. */
  scale?: number;
  /** When set (signed-in remote viewer), ✋ can use the presenter raise-hand queue. Max 80 chars on parse. */
  sender_name?: string;
  /** Firebase Auth uid (or similar) so the presenter dedupes one queue row per person across multiple remote tabs. Max 128 chars on parse. */
  sender_uid?: string;
}

/** Client build identifier for `participant_status` payloads. */
export const REMOTE_V1_CLIENT_VERSION = '1';

/** Republish retained `online` participant status this often so brokers / aggregators see a fresh envelope `ts`. */
export const REMOTE_V1_STATUS_HEARTBEAT_MS = 45_000;

export type RemoteV1ParticipantConnectionStatus = 'online' | 'idle' | 'reconnecting' | 'offline';

export interface RemoteV1ParticipantStatusPayload {
  participant_id: string;
  role: string;
  status: RemoteV1ParticipantConnectionStatus;
  client?: { kind: string; version: string };
}

function seg(id: string): string {
  return encodeURIComponent(String(id || '').trim() || 'deck');
}

export function topicPresentationPresence(presentationId: string): string {
  return `${REMOTE_V1_NAMESPACE}/presentation/${seg(presentationId)}/presence`;
}

export function topicSessionPresence(presentationId: string, sessionId: string): string {
  return `${REMOTE_V1_NAMESPACE}/presentation/${seg(presentationId)}/session/${seg(sessionId)}/presence`;
}

export function topicSessionState(presentationId: string, sessionId: string): string {
  return `${REMOTE_V1_NAMESPACE}/presentation/${seg(presentationId)}/session/${seg(sessionId)}/state`;
}

export function topicSessionCommand(presentationId: string, sessionId: string): string {
  return `${REMOTE_V1_NAMESPACE}/presentation/${seg(presentationId)}/session/${seg(sessionId)}/command`;
}

export function topicSessionEmote(presentationId: string, sessionId: string): string {
  return `${REMOTE_V1_NAMESPACE}/presentation/${seg(presentationId)}/session/${seg(sessionId)}/emote`;
}

export function topicParticipantStatus(
  presentationId: string,
  sessionId: string,
  participantId: string
): string {
  return `${REMOTE_V1_NAMESPACE}/presentation/${seg(presentationId)}/session/${seg(sessionId)}/participant/${seg(participantId)}/status`;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

/**
 * Best-effort counts from `t: "presence"` payload (session or presentation topic).
 * Populated when a presence aggregator (or broker rules) publishes retained JSON.
 */
export function parseRemoteV1PresenceCounts(payload: unknown): {
  viewersTotal?: number;
  viewers?: number;
  presentersTotal?: number;
} | null {
  if (!isRecord(payload)) return null;
  const counts = payload.counts;
  if (!isRecord(counts)) return null;
  const viewersTotal = typeof counts.viewers_total === 'number' ? counts.viewers_total : undefined;
  const viewers = typeof counts.viewers === 'number' ? counts.viewers : undefined;
  const presentersTotal = typeof counts.presenters_total === 'number' ? counts.presenters_total : undefined;
  if (viewersTotal === undefined && viewers === undefined && presentersTotal === undefined) return null;
  return { viewersTotal, viewers, presentersTotal };
}

export function isRemoteV1WireEnvelope(raw: unknown): raw is RemoteV1WireEnvelope {
  if (!isRecord(raw)) return false;
  if (raw.v !== REMOTE_V1_ENVELOPE_VERSION) return false;
  const t = raw.t;
  if (
    t !== 'state' &&
    t !== 'presence' &&
    t !== 'command' &&
    t !== 'emote' &&
    t !== 'participant_status'
  ) {
    return false;
  }
  if (typeof raw.presentation_id !== 'string' || !raw.presentation_id) return false;
  if (typeof raw.ts !== 'number') return false;
  if (typeof raw.session_id !== 'string') return false;
  /* Presentation-level `presence` may use empty `session_id` (aggregator / tooling). */
  if (t !== 'presence' && !String(raw.session_id).trim()) return false;
  return true;
}

export function parseRemoteV1StatePayload(payload: unknown): RemoteV1StatePayload | null {
  if (!isRecord(payload)) return null;
  const slide = payload.slide;
  if (!isRecord(slide)) return null;
  const h = Number(slide.h);
  const v = Number(slide.v);
  const f = Number(slide.f);
  if (!Number.isFinite(h) || !Number.isFinite(v) || !Number.isFinite(f)) return null;
  if (typeof payload.mode !== 'string') return null;
  const version = Number(payload.version);
  if (!Number.isFinite(version)) return null;
  const updated_at = String(payload.updated_at || '');
  if (!updated_at) return null;
  const meta = payload.meta;
  let metaOut: RemoteV1StateMeta | undefined;
  if (meta !== undefined) {
    if (!isRecord(meta)) return null;
    metaOut = {
      title: typeof meta.title === 'string' ? meta.title : undefined,
      notes_plain: typeof meta.notes_plain === 'string' ? meta.notes_plain : undefined,
      slides_version: typeof meta.slides_version === 'string' ? meta.slides_version : undefined,
      total_slides: typeof meta.total_slides === 'number' ? meta.total_slides : undefined,
      highlight_color_rgb:
        typeof meta.highlight_color_rgb === 'string' ? meta.highlight_color_rgb : undefined,
      tts_lang: typeof meta.tts_lang === 'string' ? meta.tts_lang : undefined,
      notes_source_lang:
        typeof meta.notes_source_lang === 'string' ? meta.notes_source_lang : undefined
    };
  }
  return {
    slide: { h, v, f },
    mode: payload.mode,
    meta: metaOut,
    version,
    updated_at
  };
}

export function parseRemoteV1CommandPayload(payload: unknown): RemoteV1CommandPayload | null {
  if (!isRecord(payload)) return null;
  const type = payload.type;
  const allowed: RemoteV1CommandType[] = [
    'NAVIGATE_NEXT',
    'NAVIGATE_PREV',
    'NAVIGATE_UP',
    'NAVIGATE_DOWN',
    'SET_SLIDE',
    'TTS_RUN',
    'TTS_SETTINGS',
    'SET_MODE'
  ];
  if (typeof type !== 'string' || !allowed.includes(type as RemoteV1CommandPayload['type'])) {
    return null;
  }
  const out: RemoteV1CommandPayload = { type: type as RemoteV1CommandPayload['type'] };
  if (payload.slide !== undefined) {
    const s = payload.slide;
    if (!isRecord(s)) return null;
    const h = Number(s.h);
    const v = Number(s.v);
    const f = Number(s.f);
    if (!Number.isFinite(h) || !Number.isFinite(v) || !Number.isFinite(f)) return null;
    out.slide = { h, v, f };
  }
  if (typeof payload.codeword === 'string') out.codeword = payload.codeword;
  if (typeof payload.lang === 'string') out.lang = payload.lang;
  if (typeof payload.mode === 'string') out.mode = payload.mode;
  return out;
}

const EMOTE_SCALE_MIN = 1;
const EMOTE_SCALE_MAX = 2.25;

export function parseRemoteV1EmotePayload(payload: unknown): RemoteV1EmotePayload | null {
  if (!isRecord(payload)) return null;
  if (typeof payload.emoji !== 'string' || !payload.emoji) return null;
  const out: RemoteV1EmotePayload = {
    emoji: payload.emoji,
    label: typeof payload.label === 'string' ? payload.label : undefined,
    ttl_ms: typeof payload.ttl_ms === 'number' ? payload.ttl_ms : undefined
  };
  if (typeof payload.sender_name === 'string') {
    const sn = payload.sender_name.trim().slice(0, 80);
    if (sn) out.sender_name = sn;
  }
  if (typeof payload.sender_uid === 'string') {
    const su = payload.sender_uid.trim().slice(0, 128);
    if (su) out.sender_uid = su;
  }
  const sc = payload.scale;
  if (typeof sc === 'number' && Number.isFinite(sc)) {
    const clamped = Math.min(EMOTE_SCALE_MAX, Math.max(EMOTE_SCALE_MIN, sc));
    if (clamped !== 1) out.scale = clamped;
  }
  return out;
}

export function buildStateEnvelope(params: {
  sessionId: string;
  presentationId: string;
  participantId: string;
  role: string;
  state: RemoteV1StatePayload;
}): RemoteV1WireEnvelope {
  return {
    v: REMOTE_V1_ENVELOPE_VERSION,
    t: 'state',
    session_id: params.sessionId,
    presentation_id: params.presentationId,
    ts: Date.now(),
    actor: { participant_id: params.participantId, role: params.role },
    payload: params.state
  };
}

export function buildCommandEnvelope(params: {
  sessionId: string;
  presentationId: string;
  participantId: string;
  role: string;
  command: RemoteV1CommandPayload;
  commandToken?: string;
}): RemoteV1WireEnvelope {
  const env: RemoteV1WireEnvelope = {
    v: REMOTE_V1_ENVELOPE_VERSION,
    t: 'command',
    session_id: params.sessionId,
    presentation_id: params.presentationId,
    ts: Date.now(),
    actor: { participant_id: params.participantId, role: params.role },
    payload: params.command
  };
  if (params.commandToken) env.command_token = params.commandToken;
  return env;
}

export function buildEmoteEnvelope(params: {
  sessionId: string;
  presentationId: string;
  participantId: string;
  role: string;
  emote: RemoteV1EmotePayload;
}): RemoteV1WireEnvelope {
  return {
    v: REMOTE_V1_ENVELOPE_VERSION,
    t: 'emote',
    session_id: params.sessionId,
    presentation_id: params.presentationId,
    ts: Date.now(),
    actor: { participant_id: params.participantId, role: params.role },
    payload: params.emote
  };
}

export function buildParticipantStatusEnvelope(params: {
  sessionId: string;
  presentationId: string;
  participantId: string;
  role: string;
  payload: RemoteV1ParticipantStatusPayload;
}): RemoteV1WireEnvelope {
  return {
    v: REMOTE_V1_ENVELOPE_VERSION,
    t: 'participant_status',
    session_id: params.sessionId,
    presentation_id: params.presentationId,
    ts: Date.now(),
    actor: { participant_id: params.participantId, role: params.role },
    payload: params.payload
  };
}
